// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, X, Gift } from 'lucide-react';

import {
  Card,
  Input,
  Button,
  InfoBanner,
  Select,
  Badge,
  ConfirmDialog,
  workflowTableIconButtonEditClass,
  workflowTableIconButtonDangerClass,
} from '../../components/WorkflowFormPrimitives';
import { useCompanyContext } from '../../context/CompanyContext';
import { fmt } from '../../utils/formatCurrency';
import { calcBuyerEquityAwardParachuteValue } from '../../utils/buyerEquityAwardValue';

export const BuyerEquityAwards = ({ formData, setFormData }) => {
  const { currentAnalysisId } = useCompanyContext();
  const individuals = formData.individuals || [];
  const uniqueNames = Array.from(new Set(individuals.map((i: { name: string }) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const [data, setData] = useState(formData.buyerEquityData || {});
  const lastIncomingBuyerEqJson = useRef(null);

  useEffect(() => {
    lastIncomingBuyerEqJson.current = null;
  }, [currentAnalysisId]);

  useEffect(() => {
    const incoming = formData.buyerEquityData || {};
    const json = JSON.stringify(incoming);
    if (lastIncomingBuyerEqJson.current !== json) {
      lastIncomingBuyerEqJson.current = json;
      setData(incoming);
    }
  }, [formData.buyerEquityData]);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAwardId, setEditingAwardId] = useState(null);
  const [editingIndId, setEditingIndId] = useState(null);
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const emptyAward = {
    type: 'rsu',
    shares: '',
    fmvPerShare: '',
    exercisePrice: '',
    volatility: 'medium',
    remainingTermMonths: '60',
  };
  const [awardForm, setAwardForm] = useState(emptyAward);
  const [buyerAwardDeleteConfirm, setBuyerAwardDeleteConfirm] = useState(null);

  const calcAwardValue = (award) => calcBuyerEquityAwardParachuteValue(award);

  const openAddModal = () => {
    setEditMode(false);
    setEditingAwardId(null);
    setEditingIndId(null);
    setSelectedIndividual('');
    setAwardForm(emptyAward);
    setShowModal(true);
  };

  const openEditModal = (indId, award) => {
    setEditMode(true);
    setEditingAwardId(award.id);
    setEditingIndId(indId);
    setSelectedIndividual(indId);
    setAwardForm({ ...award });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingAwardId(null);
    setEditingIndId(null);
  };

  const saveAward = () => {
    if (!selectedIndividual) return;
    if (editMode && editingAwardId) {
      setData((p) => ({
        ...p,
        [editingIndId]: {
          ...p[editingIndId],
          awards: (p[editingIndId]?.awards || []).map((a) =>
            a.id === editingAwardId ? { ...awardForm, id: editingAwardId } : a
          ),
        },
      }));
    } else {
      setData((p) => {
        const indData = p[selectedIndividual] || { awards: [] };
        const awards = [...(indData.awards || []), { ...awardForm, id: Date.now() }];
        return { ...p, [selectedIndividual]: { ...indData, awards } };
      });
    }
    closeModal();
  };

  const removeAward = (indId, awardId) => {
    setData((p) => ({
      ...p,
      [indId]: { ...p[indId], awards: (p[indId]?.awards || []).filter((a) => a.id !== awardId) },
    }));
  };

  const requestRemoveBuyerAward = (indId, awardId, individualName, award) => {
    const typeLabel = award.type === 'option' ? 'Option' : 'RSU';
    setBuyerAwardDeleteConfirm({
      indId,
      awardId,
      individualName,
      typeLabel,
      line: `${(+award.shares || 0).toLocaleString()} @ $${award.fmvPerShare}`,
      valueLabel: fmt(calcAwardValue(award)),
    });
  };

  const confirmRemoveBuyerAward = () => {
    if (!buyerAwardDeleteConfirm) return;
    removeAward(buyerAwardDeleteConfirm.indId, buyerAwardDeleteConfirm.awardId);
    setBuyerAwardDeleteConfirm(null);
  };

  useEffect(() => {
    setFormData((p) => ({ ...p, buyerEquityData: data }));
  }, [data]);

  const getAwardCount = () =>
    (Object.values(data) as { awards?: unknown[] }[]).reduce((sum, d) => sum + (d.awards?.length || 0), 0);
  const getTotalValue = (indId) => (data[indId]?.awards || []).reduce((sum, a) => sum + calcAwardValue(a), 0);

  return (
    <div className="space-y-4">
      <InfoBanner showIcon>
        <p className="text-xs leading-relaxed">
          <strong className="font-semibold">Buyer Equity Awards.</strong> New equity grants from the buyer. Options are
          valued using Rev. Proc. 2003-68 safe harbor.
        </p>
      </InfoBanner>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-600">{getAwardCount()} award(s) added</div>
        <Button variant="secondary" size="sm" onClick={openAddModal}>
          <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add Buyer Award
        </Button>
      </div>
      {uniqueIndividuals.map((ind) => {
        const awards = data[ind.id]?.awards || [];
        if (awards.length === 0) return null;
        return (
          <Card key={ind.id} padding={false}>
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <h4 className="text-xs font-semibold text-gray-900">{ind.name}</h4>
                  {ind.title ? <span className="text-xs text-gray-500">{ind.title}</span> : null}
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {awards.length} buyer award{awards.length === 1 ? '' : 's'}
                </p>
              </div>
              <span className="text-xs font-medium text-emerald-700 tabular-nums shrink-0">
                Total: {fmt(getTotalValue(ind.id))}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-center text-xs">
                <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Shares / FMV</th>
                    <th className="px-3 py-2">Parachute Value</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                  {awards.map((award) => (
                    <tr key={award.id} className="hover:bg-gray-50/80">
                      <td className="px-3 py-2.5">
                        <Badge size="table" variant={award.type === 'option' ? 'equityOption' : 'equityRsu'}>
                          {award.type === 'option' ? 'Option' : 'RSU'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700">
                        {(+award.shares).toLocaleString()} @ ${award.fmvPerShare}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-emerald-700">{fmt(calcAwardValue(award))}</td>
                      <td className="px-3 py-2.5">
                        <div className="inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(ind.id, award)}
                            className={workflowTableIconButtonEditClass}
                            title="Edit award"
                            aria-label="Edit award"
                          >
                            <Pencil size={18} strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => requestRemoveBuyerAward(ind.id, award.id, ind.name, award)}
                            className={workflowTableIconButtonDangerClass}
                            title="Remove award"
                            aria-label="Remove award"
                          >
                            <Trash2 size={18} strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
      {getAwardCount() === 0 && (
        <Card className="py-8 px-4 text-center">
          <Gift size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-xs text-gray-500">No buyer equity awards added yet</p>
          <div className="flex justify-center mt-3">
            <Button variant="secondary" size="sm" onClick={openAddModal}>
              <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add First Award
            </Button>
          </div>
        </Card>
      )}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal}></div>
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <h3 className="text-xs font-medium text-gray-900">
                {editMode ? 'Edit Buyer Equity Award' : 'Add Buyer Equity Award'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} aria-hidden />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <Select
                label="Individual"
                value={selectedIndividual}
                onChange={(e) => setSelectedIndividual(e.target.value)}
                disabled={editMode}
              >
                <option value="">Select individual...</option>
                {uniqueIndividuals.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                    {ind.title ? ` — ${ind.title}` : ''}
                  </option>
                ))}
              </Select>
              <Select
                label="Award Type"
                value={awardForm.type}
                onChange={(e) => setAwardForm((p) => ({ ...p, type: e.target.value }))}
              >
                <option value="rsu">RSU</option>
                <option value="option">Stock Option</option>
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Number of Shares"
                  type="number"
                  value={awardForm.shares}
                  onChange={(e) => setAwardForm((p) => ({ ...p, shares: e.target.value }))}
                  placeholder="0"
                />
                <Input
                  label="FMV per Share"
                  type="number"
                  step="0.01"
                  value={awardForm.fmvPerShare}
                  onChange={(e) => setAwardForm((p) => ({ ...p, fmvPerShare: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              {awardForm.type === 'option' && (
                <>
                  <Input
                    label="Exercise Price"
                    type="number"
                    step="0.01"
                    value={awardForm.exercisePrice}
                    onChange={(e) => setAwardForm((p) => ({ ...p, exercisePrice: e.target.value }))}
                    placeholder="0.00"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      label="Stock Volatility"
                      value={awardForm.volatility}
                      onChange={(e) => setAwardForm((p) => ({ ...p, volatility: e.target.value }))}
                    >
                      <option value="low">Low (≤30%)</option>
                      <option value="medium">Medium (30-70%)</option>
                      <option value="high">High (≥70%)</option>
                    </Select>
                    <Input
                      label="Remaining Term (months)"
                      type="number"
                      value={awardForm.remainingTermMonths}
                      onChange={(e) => setAwardForm((p) => ({ ...p, remainingTermMonths: e.target.value }))}
                      placeholder="60"
                    />
                  </div>
                  <InfoBanner>
                    Options valued using Rev. Proc. 2003-68 safe harbor. Estimated value:{' '}
                    <strong>{fmt(calcAwardValue(awardForm))}</strong>
                  </InfoBanner>
                </>
              )}
            </div>
            <div className="flex justify-end space-x-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg shrink-0">
              <Button variant="secondary" size="sm" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveAward}
                disabled={!selectedIndividual || !awardForm.shares || !awardForm.fmvPerShare}
              >
                {editMode ? 'Save Changes' : 'Add Award'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!buyerAwardDeleteConfirm}
        title="Remove Buyer Equity Award"
        description={
          buyerAwardDeleteConfirm
            ? `Remove this ${buyerAwardDeleteConfirm.typeLabel} award for ${buyerAwardDeleteConfirm.individualName}? This cannot be undone.`
            : undefined
        }
        confirmLabel="Remove Award"
        onCancel={() => setBuyerAwardDeleteConfirm(null)}
        onConfirm={confirmRemoveBuyerAward}
      >
        {buyerAwardDeleteConfirm ? (
          <ul className="space-y-1.5 rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs text-gray-700">
            <li>
              <span className="text-gray-500">Position: </span>
              <span className="font-medium tabular-nums">{buyerAwardDeleteConfirm.line}</span>
            </li>
            <li>
              <span className="text-gray-500">Parachute value: </span>
              <span className="font-medium text-emerald-700 tabular-nums">{buyerAwardDeleteConfirm.valueLabel}</span>
            </li>
          </ul>
        ) : null}
      </ConfirmDialog>
    </div>
  );
};
